import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FaUserCircle,
  FaThLarge,
  FaSun,
  FaMoon,
  FaRegCalendarAlt,
  FaBuilding,
  FaBriefcase,
  FaLifeRing,
  FaStar,
  FaChartPie,
  FaUsers,
  FaTruck,
  FaGift,
} from 'react-icons/fa';
import {
  FiCheck,
  FiCheckCircle,
  FiTrash2,
  FiRotateCcw,
  FiSearch,
  FiMenu,
  FiX,
  FiEye,
  FiLifeBuoy,
  FiBell,
  FiGift,
  FiUser,
  FiBriefcase,
  FiBarChart2,
  FiStar,
  FiSliders,
  FiSettings,
  FiLogOut,
} from 'react-icons/fi';
import PremiumLogo from './PremiumLogo';
import '../App.css';
import {
  fetchQuickSearchSuggestions,
  getQuickSearchDefaultSuggestions,
} from '../services/searchApi';
import { buildUserAvatarUrl } from '../services/profileApi';
import { UNIT_TYPES } from '../constants/unitTypes';
import { getCompanyPortalPath } from '../constants/companyRoutes';
import { normalizeMojibake } from '../utils/textNormalization';
import {
  formatNotificationDate,
  isProposalNotification,
  proposalNotificationHasClientContact,
  resolveNotificationIcon,
  resolveNotificationTarget,
  resolveProposalNotificationUrl,
} from '../utils/notificationHelpers';

const STOP_WORDS = new Set([
  'de',
  'del',
  'la',
  'el',
  'los',
  'las',
  'una',
  'un',
  'y',
  'o',
  'en',
  'con',
  'para',
  'por',
  'al',
]);

const ENABLE_DESKTOP_CONTEXT_BAR = false;

function normalizeText(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');
}

function tokenize(value) {
  return normalizeText(value)
    .replace(/[^a-z0-9\s]/gi, ' ')
    .split(/\s+/)
    .filter(token => {
      if (!token) return false;
      if (/\d/.test(token)) return true;
      if (STOP_WORDS.has(token)) return false;
      return token.length > 2;
    });
}

function matchesNormalizedLabel(labelNormalized, normalizedTerm) {
  return (
    labelNormalized === normalizedTerm ||
    labelNormalized.includes(normalizedTerm) ||
    normalizedTerm.includes(labelNormalized)
  );
}

const sanitizeProposalNotificationMessage = message => {
  if (!message) return '';
  const contactMarkers = ['Contacto:', 'Correo:', 'Teléfono:', 'Telefono:', 'Teléfonos:'];
  let sanitized = message;
  contactMarkers.forEach(marker => {
    const index = sanitized.indexOf(marker);
    if (index !== -1) {
      sanitized = sanitized.slice(0, index).trim();
    }
  });
  if (!sanitized) return '';
  if (/[.,]$/.test(sanitized)) {
    return sanitized.replace(/[.,]+\s*$/, '.');
  }
  return sanitized;
};

let unitTypeSearchIndex = null;

function getUnitTypeSearchIndex() {
  if (unitTypeSearchIndex) {
    return unitTypeSearchIndex;
  }
  unitTypeSearchIndex = UNIT_TYPES.map(option => {
    const tokens = tokenize([option.label, option.value].join(' '));
    const normalizedLabel = normalizeText(option.label);
    return {
      ...option,
      searchTokens: tokens,
      normalizedLabel,
    };
  });
  return unitTypeSearchIndex;
}

function buildSuggestionList(rawList, query) {
  if (!query) {
    return Array.isArray(rawList) ? rawList : [];
  }

  const normalizedQuery = normalizeText(query);
  const queryTokens = tokenize(normalizedQuery);
  const collected = [];
  const seen = new Set();
  let searchOption = null;

  (Array.isArray(rawList) ? rawList : []).forEach(item => {
    if (!item || !item.id) return;
    if (item.type === 'search') {
      searchOption = item;
      return;
    }
    if (seen.has(item.id)) return;
    seen.add(item.id);
    collected.push(item);
  });

  if (queryTokens.length) {
    const unitMatches = getUnitTypeSearchIndex().filter(option => {
      if (!option) return false;
      if (option.normalizedLabel.includes(normalizedQuery)) return true;
      return queryTokens.every(token =>
        option.searchTokens.some(candidate => candidate.includes(token))
      );
    })
      .slice(0, 5)
      .map(option => ({
        id: `unit-${option.value}`,
        type: 'unitType',
        categoryLabel: 'Unidades',
        title: option.label,
        subtitle: 'Tipo de unidad',
        icon: 'unitType',
        searchValue: option.label,
        unitTypeValue: option.value,
      }));

    unitMatches.forEach(item => {
      if (seen.has(item.id)) return;
      seen.add(item.id);
      collected.push(item);
    });
  }

  if (searchOption) {
    collected.push(searchOption);
  } else if (!collected.length) {
    collected.push({
      id: `search-${normalizedQuery}`,
      type: 'search',
      categoryLabel: 'Búsquedas',
      title: `Buscar "${query}" en Marketplace`,
      subtitle: 'Ver resultados completos en Marketplace',
      icon: 'search',
      searchValue: query,
    });
  }

  return collected;
}

function findSuggestionMatch(list, normalizedTerm) {
  if (!normalizedTerm || !Array.isArray(list)) return null;
  return list.find(item => {
    if (!item || !item.type || !item.title) return false;
    if (item.type === 'search') return false;
    if (item.type === 'company' || item.type === 'profile' || item.type === 'unitType') {
      const labelNormalized = normalizeText(item.title);
      return matchesNormalizedLabel(labelNormalized, normalizedTerm);
    }
    return false;
  });
}

const SUGGESTION_ICON_MAP = {
  search: FiSearch,
  section: FaThLarge,
  dashboard: FaThLarge,
  marketplace: FaBriefcase,
  requests: FaBriefcase,
  calendar: FaRegCalendarAlt,
  company: FaBuilding,
  publication: FaBriefcase,
  request: FaRegCalendarAlt,
  profile: FaUserCircle,
  feed: FaThLarge,
  solicitudes: FaBriefcase,
  reservas: FaRegCalendarAlt,
  unidades: FaBriefcase,
  publicaciones: FaBriefcase,
  support: FaLifeRing,
  faq: FaLifeRing,
  suscripciones: FaStar,
  promocionar: FaStar,
  empresa: FaBuilding,
  calendario: FaRegCalendarAlt,
  estadisticas: FaChartPie,
  statistics: FaChartPie,
  reportes: FaChartPie,
  'estadisticas-reportes': FaChartPie,
  'estadisticas-clientes': FaUsers,
  clientes: FaUsers,
  proveedores: FaTruck,
  'estadisticas-proveedores': FaTruck,
  unitType: FaTruck,
  referrals: FaGift,
};

const SHOULD_RENDER_THEME_TOGGLE = false;

const Topbar = ({
  user,
  displayName,
  onSignOut,
  theme = 'light',
  onToggleTheme,
  companyNotifications = [],
  notificationsHydrated = false,
  onCompanyNotificationsOpen,
  onNotificationRead,
  onNotificationUnread,
  onNotificationDelete,
  onMarkAllNotificationsRead,
  }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [quickSearchQuery, setQuickSearchQuery] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [activeNavSection, setActiveNavSection] = useState('home');
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [proposalNotificationDetail, setProposalNotificationDetail] = useState({
    open: false,
    item: null,
  });
  const formatUnreadNotificationsLabel = useCallback(count => {
    if (!count || count <= 0) return '';
    const safeCount = count > 99 ? 99 : count;
    const plural = safeCount === 1 ? 'notificación' : 'notificaciones';
    return `${safeCount} ${plural} sin leer`;
  }, []);
  const location = useLocation();
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const notificationsRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchContainerRef = useRef(null);
  const mobileSearchInputRef = useRef(null);
  const suggestionsFetchIdRef = useRef(0);
  const searchDebounceRef = useRef(null);
  const lastLocationRef = useRef({
    path: location.pathname,
    search: location.search,
  });
  const isAdmin = user?.role === 'admin';
  const userRole = String(user?.role || '').toLowerCase();
  const isClientRole = userRole === 'client';
  const userAudience = isAdmin ? 'admin' : isClientRole ? 'client' : 'provider';
  const filterByAudience = useCallback(
    items =>
      (Array.isArray(items) ? items : []).filter(item => {
        if (!Array.isArray(item?.audiences) || item.audiences.length === 0) {
          return true;
        }
        if (userAudience === 'admin') {
          return true;
        }
        return item.audiences.includes(userAudience);
      }),
    [userAudience]
  );
  const isDarkTheme = theme === 'dark';
  const themeToggleLabel = isDarkTheme ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro';
  const primaryName =
    displayName || user?.displayName || user?.name || user?.email || 'Usuario';
  const userInitials = useMemo(() => {
    const source = String(primaryName || '').trim();
    if (!source) return 'FL';
    const parts = source.split(/\s+/).filter(Boolean);
    if (parts.length === 1) {
      const cleaned = parts[0].replace(/[^a-zA-Z0-9]/g, '');
      const solo = cleaned || parts[0];
      return solo.slice(0, 2).toUpperCase();
    }
    const first = parts[0]?.[0] || '';
    const last = parts[parts.length - 1]?.[0] || '';
    const initials = `${first}${last}`.trim();
    return initials ? initials.toUpperCase() : 'FL';
  }, [primaryName]);
  const userAvatarUrl = useMemo(() => {
    if (!user) return '';
    return buildUserAvatarUrl(user, '120x120') || user?.avatarUrl || user?.avatar || '';
  }, [user]);
  const hasUserAvatar = Boolean(userAvatarUrl);
  const notifications = useMemo(() => {
    const normalized = Array.isArray(companyNotifications) ? companyNotifications : [];
    return normalized
      .filter(Boolean)
      .sort((a, b) => {
        const dateA = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
      });
  }, [companyNotifications]);
  const notificationsReady = Boolean(notificationsHydrated);
  const emptyNotificationsCopy = notificationsReady
    ? 'No tienes notificaciones nuevas.'
    : 'Cargando tus notificaciones...';
  const unreadCount = useMemo(
    () => notifications.filter(item => item && !item.read).length,
    [notifications]
  );
  const unreadBadgeValue = useMemo(() => {
    if (!unreadCount) return '';
    return unreadCount > 99 ? '99+' : String(unreadCount);
  }, [unreadCount]);


  useEffect(() => {
    if (!menuOpen) return;
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  useEffect(() => {
    if (!notificationsOpen) return;
    function handleClickOutside(e) {
      if (notificationsRef.current && !notificationsRef.current.contains(e.target)) {
        setNotificationsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [notificationsOpen]);

  useEffect(() => {
    if (!user) {
      setQuickSearchQuery('');
      setSearchSuggestions([]);
      setIsSuggestionsOpen(false);
      setActiveSuggestionIndex(-1);
      setActiveNavSection('home');
      return;
    }
    const params = new URLSearchParams(location.search);
    const query = params.get('quickSearch') || '';
    setQuickSearchQuery(query);
  }, [location.search, user]);

  useEffect(() => {
    if (!user) {
      return undefined;
    }
    const trimmed = quickSearchQuery.trim();
    window.clearTimeout(searchDebounceRef.current);

    const fetchSuggestions = () => {
      const fetchId = ++suggestionsFetchIdRef.current;
      const loader =
        trimmed.length > 0
          ? fetchQuickSearchSuggestions(trimmed, { limitPerType: 5 })
          : Promise.resolve(getQuickSearchDefaultSuggestions());
      setSearchLoading(trimmed.length > 0);
      loader
        .then(results => {
          if (suggestionsFetchIdRef.current !== fetchId) return;
          const processed = buildSuggestionList(
            Array.isArray(results) ? results : [],
            trimmed
          );
          setSearchSuggestions(processed);
          setActiveSuggestionIndex(-1);
        })
        .catch(error => {
          console.warn('No se pudieron cargar las sugerencias rapidas.', error);
          if (suggestionsFetchIdRef.current !== fetchId) return;
          if (trimmed.length > 0) {
            setSearchSuggestions(buildSuggestionList([], trimmed));
          } else {
            setSearchSuggestions([]);
          }
          setActiveSuggestionIndex(-1);
        })
        .finally(() => {
          if (suggestionsFetchIdRef.current === fetchId) {
            setSearchLoading(false);
          }
        });
    };

    if (trimmed.length > 0) {
      searchDebounceRef.current = window.setTimeout(fetchSuggestions, 220);
    } else {
      fetchSuggestions();
    }

    return () => {
      window.clearTimeout(searchDebounceRef.current);
    };
  }, [quickSearchQuery, user]);

  useEffect(() => {
    setIsSuggestionsOpen(false);
    setActiveSuggestionIndex(-1);
  }, [location.pathname]);

  useEffect(() => {
    if (!isSuggestionsOpen) return undefined;
    const handleClickOutside = event => {
      if (!searchContainerRef.current) return;
      if (searchContainerRef.current.contains(event.target)) return;
      setIsSuggestionsOpen(false);
      setActiveSuggestionIndex(-1);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSuggestionsOpen]);


  const closeMobileSidebar = useCallback(() => {
    setIsMobileSidebarOpen(false);
    setIsSuggestionsOpen(false);
    setActiveSuggestionIndex(-1);
  }, []);
  const openMobileSidebar = useCallback(() => {
    setIsSuggestionsOpen(false);
    setActiveSuggestionIndex(-1);
    setNotificationsOpen(false);
    setMenuOpen(false);
    setIsMobileSidebarOpen(true);
  }, []);

  useEffect(() => {
    const { path, search } = lastLocationRef.current;
    const hasChanged = path !== location.pathname || search !== location.search;
    if (hasChanged && isMobileSidebarOpen) {
      closeMobileSidebar();
    }
    lastLocationRef.current = {
      path: location.pathname,
      search: location.search,
    };
  }, [location.pathname, location.search, isMobileSidebarOpen, closeMobileSidebar]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const className = 'topbar-mobile-open';
    if (isMobileSidebarOpen) {
      document.body.classList.add(className);
    } else {
      document.body.classList.remove(className);
    }
    return () => {
      document.body.classList.remove(className);
    };
  }, [isMobileSidebarOpen]);

  useEffect(() => {
    if (!isMobileSidebarOpen) return undefined;
    const handler = event => {
      if (event.key === 'Escape') {
        closeMobileSidebar();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isMobileSidebarOpen, closeMobileSidebar]);

  useEffect(() => {
    if (!isMobileSidebarOpen) return;
    const id = window.setTimeout(() => {
      if (mobileSearchInputRef.current) {
        mobileSearchInputRef.current.focus();
      }
    }, 180);
    return () => window.clearTimeout(id);
  }, [isMobileSidebarOpen]);

  const logoHref = user ? '/dashboard/feed' : '/home';
  const suggestionsListId = 'topbar-search-suggestions';

  const hideSuggestions = useCallback(
    ({ blur = false } = {}) => {
      setIsSuggestionsOpen(false);
      setActiveSuggestionIndex(-1);
      if (blur && searchInputRef.current) {
        searchInputRef.current.blur();
      }
    },
    []
  );

  const navigateWithMarketplaceFilters = useCallback(
    ({ quickSearch, unitTypes } = {}) => {
      const isDashboardRoute = location.pathname.startsWith('/dashboard');
      const applyParams = params => {
        if (quickSearch) {
          params.set('quickSearch', quickSearch);
        } else {
          params.delete('quickSearch');
        }
        params.delete('unitType');
        if (Array.isArray(unitTypes) && unitTypes.length) {
          unitTypes.forEach(value => {
            if (value) params.append('unitType', value);
          });
        }
        return params;
      };

      if (isDashboardRoute) {
        const params = applyParams(new URLSearchParams(location.search));
        params.set('section', 'marketplace');
        navigate(`/dashboard?${params.toString()}`);
      } else {
        const params = applyParams(new URLSearchParams());
        const suffix = params.toString();
        navigate(suffix ? `/marketplace?${suffix}` : '/marketplace');
      }

      if (isMobileSidebarOpen) {
        closeMobileSidebar();
      }
    },
    [closeMobileSidebar, isMobileSidebarOpen, location.pathname, location.search, navigate]
  );

  const navigateToSearchResults = useCallback(
    async value => {
      const rawValue = value ?? quickSearchQuery;
      const searchValue = String(rawValue || '').trim();

      if (!searchValue) {
        navigateWithMarketplaceFilters();
        hideSuggestions({ blur: true });
        setNotificationsOpen(false);
        setMenuOpen(false);
        return;
      }

      const normalizedTerm = normalizeText(searchValue);
      let match = findSuggestionMatch(searchSuggestions, normalizedTerm);

      if (!match) {
        try {
          const fresh = await fetchQuickSearchSuggestions(searchValue, { limitPerType: 5 });
          const processed = buildSuggestionList(
            Array.isArray(fresh) ? fresh : [],
            searchValue
          );
          setSearchSuggestions(processed);
          match = findSuggestionMatch(processed, normalizedTerm);
        } catch (error) {
          console.warn('No se pudieron cargar sugerencias adicionales.', error);
        }
      }

      if (match && (match.type === 'company' || match.type === 'profile') && match.href) {
        navigate(match.href);
      } else if (match && match.type === 'unitType' && match.unitTypeValue) {
        navigateWithMarketplaceFilters({ unitTypes: [match.unitTypeValue] });
      } else {
        navigateWithMarketplaceFilters({ quickSearch: searchValue });
      }

      hideSuggestions({ blur: true });
      setNotificationsOpen(false);
      setMenuOpen(false);
    },
    [
      hideSuggestions,
      navigate,
      navigateWithMarketplaceFilters,
      quickSearchQuery,
      searchSuggestions,
      setMenuOpen,
      setNotificationsOpen,
    ]
  );

  const navigateWithSuggestion = useCallback(
    suggestion => {
      if (!suggestion || suggestion.isPlaceholder) {
        return;
      }

      if (suggestion.type === 'company' || suggestion.type === 'profile') {
        if (suggestion.href) {
          navigate(suggestion.href);
        } else if (suggestion.searchValue) {
          navigateToSearchResults(suggestion.searchValue);
        } else {
          navigateToSearchResults();
        }
      } else if (suggestion.type === 'unitType' && suggestion.unitTypeValue) {
        navigateWithMarketplaceFilters({ unitTypes: [suggestion.unitTypeValue] });
      } else if (suggestion.type === 'search' || suggestion.searchValue) {
        navigateToSearchResults(suggestion.searchValue ?? quickSearchQuery);
      } else if (suggestion.href) {
        navigate(suggestion.href);
      }

      hideSuggestions({ blur: true });
      setNotificationsOpen(false);
      setMenuOpen(false);
      if (isMobileSidebarOpen) {
        closeMobileSidebar();
      }
    },
    [
      closeMobileSidebar,
      hideSuggestions,
      isMobileSidebarOpen,
      navigate,
      navigateToSearchResults,
      navigateWithMarketplaceFilters,
      quickSearchQuery,
      setMenuOpen,
      setNotificationsOpen,
    ]
  );

  const handleSubnavNavigate = item => {
    if (!item) return;
    if (item.href) {
      navigate(item.href);
    } else if (item.onClick) {
      item.onClick();
    }
    if (isMobileSidebarOpen) {
      closeMobileSidebar();
    }
  };

  const handleMainNavLinkClick = useCallback(() => {
    hideSuggestions();
    setNotificationsOpen(false);
    setMenuOpen(false);
    if (isMobileSidebarOpen) {
      closeMobileSidebar();
    }
  }, [closeMobileSidebar, hideSuggestions, isMobileSidebarOpen]);

  const handleSignOutClick = useCallback(async () => {
    setMenuOpen(false);
    setActiveNavSection('home');
    hideSuggestions();
    setNotificationsOpen(false);
    try {
      if (typeof onSignOut === 'function') {
        await onSignOut();
      }
    } finally {
      if (isMobileSidebarOpen) {
        closeMobileSidebar();
      }
      navigate('/');
    }
  }, [
    closeMobileSidebar,
    hideSuggestions,
    isMobileSidebarOpen,
    navigate,
    onSignOut,
  ]);


  const profileSlugCandidate = useMemo(() => {
    const username = typeof user?.username === 'string' ? user.username.trim() : '';
    if (username) return username;
    const userId = typeof user?.id === 'string' ? user.id.trim() : '';
    return userId;
  }, [user]);
  const userProfileHref = useMemo(() => {
    if (!profileSlugCandidate) return '';
    return `/usuario/${encodeURIComponent(profileSlugCandidate)}`;
  }, [profileSlugCandidate]);

  const statisticsSubnavItems = useMemo(() => {
    if (!user) return [];
    const items = [
      {
        label: 'Reportes',
        description: 'Resumen ejecutivo con desempeño y métricas',
        href: '/dashboard?section=estadisticas-reportes',
        icon: 'estadisticas-reportes',
      },
      {
        label: 'Clientes',
        description: 'Monitorea engagement y retención de clientes',
        href: '/dashboard?section=estadisticas-clientes',
        icon: 'estadisticas-clientes',
        audiences: ['client'],
      },
      {
        label: 'Proveedores',
        description: 'Analiza rendimiento comercial y capacidad',
        href: '/dashboard?section=estadisticas-proveedores',
        icon: 'estadisticas-proveedores',
        audiences: ['provider'],
      },
    ];
    return filterByAudience(items);
  }, [user, filterByAudience]);

  const accountMenuOptions = useMemo(() => {
    const options = [
      userProfileHref
        ? {
            key: 'profile',
            label: 'Mi perfil',
            icon: <FiUser />,
            onSelect: () => navigate(userProfileHref),
          }
        : null,
      {
        key: 'company',
        label: 'Mi empresa',
        icon: <FiBriefcase />,
        onSelect: () => navigate(getCompanyPortalPath(user)),
      },
      {
        key: 'statistics',
        label: 'Estadísticas',
        icon: <FiBarChart2 />,
        onSelect: () =>
          navigate(statisticsSubnavItems[0]?.href || '/dashboard?section=estadisticas-reportes'),
      },
      {
        key: 'membership',
        label: 'Membresía',
        icon: <FiStar />,
        onSelect: () => navigate('/dashboard?section=suscripciones'),
      },
      {
        key: 'settings',
        label: 'Configuración',
        icon: <FiSliders />,
        onSelect: () => navigate('/settings'),
      },
      {
        key: 'help-center',
        label: 'Centro de ayuda',
        icon: <FiLifeBuoy />,
        onSelect: () => navigate('/ayuda/preguntas-frecuentes'),
      },
    ].filter(Boolean);
    if (isAdmin) {
      options.push({
        key: 'admin-panel',
        label: 'Admin Panel',
        icon: <FiSettings />,
        onSelect: () => navigate('/admin'),
      });
    }
    options.push({
      key: 'signout',
      label: 'Cerrar sesión',
      icon: <FiLogOut />,
      onSelect: handleSignOutClick,
      variant: 'danger',
    });
    return options;
  }, [handleSignOutClick, isAdmin, navigate, statisticsSubnavItems, user, userProfileHref]);

  const mainAccountOptions = useMemo(
    () =>
      accountMenuOptions.filter(
        option => option.variant !== 'danger' && option.key !== 'admin-panel'
      ),
    [accountMenuOptions]
  );

  const adminAccountOption = useMemo(
    () => accountMenuOptions.find(option => option.key === 'admin-panel'),
    [accountMenuOptions]
  );

  const signOutOption = useMemo(
    () => accountMenuOptions.find(option => option.variant === 'danger'),
    [accountMenuOptions]
  );

  const handleAccountMenuSelect = useCallback(
    option => {
      if (!option) return;
      setMenuOpen(false);
      hideSuggestions();
      setNotificationsOpen(false);
      if (isMobileSidebarOpen) {
        closeMobileSidebar();
      }
      if (typeof option.onSelect === 'function') {
        option.onSelect();
      } else if (option.path) {
        navigate(option.path);
      }
    },
    [closeMobileSidebar, hideSuggestions, isMobileSidebarOpen, navigate]
  );

  const handleSuggestionNavigate = suggestion => {
    navigateWithSuggestion(suggestion);
  };

  const handleQuickSearchSubmit = event => {
    event.preventDefault();
    const suggestion =
      isSuggestionsOpen && activeSuggestionIndex >= 0
        ? searchSuggestions[activeSuggestionIndex]
        : null;
    if (suggestion && !suggestion.isPlaceholder) {
      handleSuggestionNavigate(suggestion);
      return;
    }
    navigateToSearchResults();
  };

  const handleSearchInputKeyDown = event => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!isSuggestionsOpen) {
        setIsSuggestionsOpen(true);
      }
      if (!searchSuggestions.length) return;
      setActiveSuggestionIndex(prev => {
        const next = prev + 1;
        if (next >= searchSuggestions.length) {
          return searchSuggestions.length - 1;
        }
        return next;
      });
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!isSuggestionsOpen) {
        setIsSuggestionsOpen(true);
      }
      if (!searchSuggestions.length) return;
      setActiveSuggestionIndex(prev => {
        const next = prev - 1;
        return next < 0 ? -1 : next;
      });
    } else if (event.key === 'Enter') {
      if (!isSuggestionsOpen) return;
      const suggestion =
        activeSuggestionIndex >= 0 ? searchSuggestions[activeSuggestionIndex] : null;
      if (suggestion && !suggestion.isPlaceholder) {
        event.preventDefault();
        handleSuggestionNavigate(suggestion);
      }
    } else if (event.key === 'Escape') {
      if (!isSuggestionsOpen) return;
      event.preventDefault();
      hideSuggestions();
    }
  };

  const homeSubnavItems = useMemo(
    () =>
      filterByAudience(
        [
          {
            label: 'Feed',
            description: 'Actividad reciente, noticias y alertas',
            href: '/dashboard?section=feed',
            icon: 'feed',
          },
          {
            label: 'Marketplace',
            description: 'Explora unidades disponibles y publicaciones',
            href: '/marketplace',
            icon: 'marketplace',
          },
        ].filter(Boolean)
      ),
    [filterByAudience]
  );

  const operationsSubnavItems = useMemo(() => {
    const reservationItem = isClientRole
      ? {
          label: 'Mis reservas',
          description: 'Consulta tus reservas creadas como cliente',
          href: '/dashboard?section=reservas-cliente',
          icon: 'reservas',
          audiences: ['client'],
        }
      : {
          label: 'Reservas',
          description: 'Consulta y administra reservas confirmadas',
          href: '/dashboard?section=reservas',
          icon: 'reservas',
          audiences: ['provider'],
        };

    return filterByAudience([
      {
        label: 'Flotilla',
        description: 'Controla y actualiza tus unidades registradas',
        href: '/dashboard?section=unidades',
        icon: 'unidades',
        audiences: ['provider'],
      },
      {
        label: 'Publicaciones',
        description: 'Edita y publica inventario operativo en minutos',
        href: '/dashboard?section=publicaciones',
        icon: 'publicaciones',
        audiences: ['provider'],
      },
      reservationItem,
      {
        label: 'Centro de solicitudes',
        description: 'Gestiona solicitudes entrantes y su estado',
        href: '/dashboard?section=solicitudes',
        icon: 'solicitudes',
        audiences: ['provider'],
      },
      {
        label: 'Calendario',
        description: 'Organiza la agenda y tareas operativas clave',
        href: '/dashboard?section=calendario',
        icon: 'calendario',
        audiences: ['provider'],
      },
    ]);
  }, [filterByAudience, isClientRole]);

  const promoteSubnavItems = useMemo(
    () =>
      filterByAudience([
        {
          label: 'Empresa',
          description: 'Destaca tu perfil usando créditos corporativos',
          href: '/dashboard?section=promocionar&target=company',
          icon: 'empresa',
          audiences: ['provider'],
        },
        {
          label: 'Publicaciones',
          description: 'Impulsa ofertas activas con tus créditos',
          href: '/dashboard?section=promocionar&target=publications',
          icon: 'promocionar',
          audiences: ['provider'],
        },
        {
          label: 'Recargar créditos',
          description: 'Compra nuevos créditos para impulsar campañas',
          href: '/checkout/credits',
          icon: 'suscripciones',
          audiences: ['provider'],
        },
        {
          label: 'Membresía Flotte',
          description: 'Gestiona y mejora tu membresía en Flotte',
          href: '/dashboard?section=suscripciones',
          icon: 'suscripciones',
          audiences: ['provider'],
        },
      ]),
    [filterByAudience]
  );

  const helpSubnavItems = useMemo(
    () => [
      {
        label: 'Preguntas frecuentes',
        description: 'Resuelve dudas comunes con respuestas rápidas',
        href: '/ayuda/preguntas-frecuentes',
        icon: 'faq',
      },
      {
        label: 'Mis tickets',
        description: 'Da seguimiento puntual a tus solicitudes abiertas',
        href: '/dashboard?section=support-tickets',
        icon: 'support',
      },
      {
        label: 'Crear ticket',
        description: 'Reporta incidentes o configura necesidades nuevas',
        href: '/dashboard?section=support-new',
        icon: 'support',
      },
      {
        label: 'Tutoriales',
        description: 'Consulta guías paso a paso para dominar Flotte',
        href: '/tutoriales',
        icon: 'support',
      },
    ],
    []
  );

  const primaryNavSections = useMemo(
    () =>
      [
        homeSubnavItems.length
          ? { key: 'home', label: 'Home', items: homeSubnavItems }
          : null,
        operationsSubnavItems.length
          ? { key: 'operations', label: 'Operaciones', items: operationsSubnavItems }
          : null,
        statisticsSubnavItems.length
          ? { key: 'statistics', label: 'Estadísticas', items: statisticsSubnavItems }
          : null,
        helpSubnavItems.length
          ? { key: 'help', label: 'Ayuda', items: helpSubnavItems }
          : null,
        promoteSubnavItems.length
          ? { key: 'promote', label: 'Promocionar', items: promoteSubnavItems }
          : null,
      ].filter(Boolean),
    [
      helpSubnavItems,
      homeSubnavItems,
      operationsSubnavItems,
      promoteSubnavItems,
      statisticsSubnavItems,
    ]
  );

  const mainNavigationLinks = useMemo(() => {
    const operationsHref =
      operationsSubnavItems[0]?.href ||
      (isClientRole ? '/dashboard?section=reservas-cliente' : '/dashboard?section=reservas');
    const promoteHref = promoteSubnavItems[0]?.href || '/dashboard?section=promocionar';
    const links = [
      { key: 'home', label: 'Home', href: '/' },
      { key: 'feed', label: 'Feed', href: '/dashboard?section=feed' },
      { key: 'marketplace', label: 'Marketplace', href: '/marketplace' },
      {
        key: 'operations',
        label: 'Operaciones',
        href: operationsHref,
      },
    ];
    if (user) {
      links.push({
        key: 'promote',
        label: 'Promocionar',
        href: promoteHref,
      });
    }
    return links;
  }, [isClientRole, operationsSubnavItems, promoteSubnavItems, user]);

  const isLinkActive = useCallback(
    href => {
      if (!href) return false;
      const [path, queryString] = href.split('?');
      const normalizedPath = path || '';
      if (normalizedPath && location.pathname !== normalizedPath) {
        return false;
      }
      if (!queryString) {
        return location.pathname === href;
      }
      const targetParams = new URLSearchParams(queryString);
      const currentParams = new URLSearchParams(location.search);
      for (const [key, value] of targetParams.entries()) {
        if (currentParams.get(key) !== value) {
          return false;
        }
      }
      return true;
    },
    [location.pathname, location.search]
  );

  useEffect(() => {
    if (!primaryNavSections.length) return;
    const matchedSection = primaryNavSections.find(section =>
      section.items.some(item => isLinkActive(item.href))
    );
    if (matchedSection) {
      setActiveNavSection(prev => (prev === matchedSection.key ? prev : matchedSection.key));
    }
  }, [isLinkActive, primaryNavSections]);

  useEffect(() => {
    if (!primaryNavSections.length) {
      setActiveNavSection('');
      return;
    }
    setActiveNavSection(prev => {
      const exists = primaryNavSections.some(section => section.key === prev);
      return exists ? prev : primaryNavSections[0].key;
    });
  }, [primaryNavSections]);

  const contextItems = useMemo(() => {
    if (!activeNavSection) return [];
    const activeSection = primaryNavSections.find(section => section.key === activeNavSection);
    return Array.isArray(activeSection?.items) ? activeSection.items : [];
  }, [activeNavSection, primaryNavSections]);
  const hasContextBar = ENABLE_DESKTOP_CONTEXT_BAR && Boolean(user && contextItems.length);
  const contextActions = useMemo(() => [], []);
  const handleContextAction = action => {
    if (!action) return;
    if (typeof action.onClick === 'function') {
      action.onClick();
      return;
    }
    if (action.to) {
      navigate(action.to);
    }
  };

  const renderContextBar = () => {
    if (!hasContextBar) return null;
    const contextBarClassName = `topbar-context-bar${
      contextActions.length ? '' : ' topbar-context-bar--solo'
    }`;
    return (
      <div className={contextBarClassName} role="navigation" aria-label="Barra contextual">
        <div className="topbar-context-left">
          <div className="topbar-context-scroll">
            {contextItems.map(item => {
              const IconComponent = SUGGESTION_ICON_MAP[item.icon] || FiSearch;
              const isActive = isLinkActive(item.href);
              return (
                <button
                  key={`${activeNavSection}-${item.label}`}
                  type="button"
                  className={`topbar-context-pill${isActive ? ' is-active' : ''}`}
                  onClick={() => handleSubnavNavigate(item)}
                >
                  <span className="topbar-context-pill-icon" aria-hidden="true">
                    <IconComponent />
                  </span>
                  <span className="topbar-context-pill-label">{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>
        {contextActions.length ? (
          <div className="topbar-context-actions">
            {contextActions.map(action => {
              const IconComponent = action.icon;
              const variantClass = action.variant
                ? ` topbar-context-action--${action.variant}`
                : '';
              const iconOnlyClass = action.iconOnly ? ' topbar-context-action--icon' : '';
              return (
                <button
                  key={action.key}
                  type="button"
                  className={`topbar-context-action${variantClass}${iconOnlyClass}`}
                  onClick={() => handleContextAction(action)}
                  aria-label={action.iconOnly ? action.ariaLabel || action.label : undefined}
                >
                  {IconComponent ? <IconComponent aria-hidden="true" /> : null}
                  {!action.iconOnly ? <span>{action.label}</span> : null}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    );
  };

  const renderSearchSuggestions = () => {
    if (!isSuggestionsOpen) return null;
    const hasResults = searchSuggestions.length > 0;
    let lastCategory = '';
    return (
      <div
        className="topbar-search-dropdown"
        id={suggestionsListId}
        role="listbox"
      >
        {searchLoading ? (
          <div className="topbar-search-loading" role="status">
            Buscando…
          </div>
        ) : null}
        {!searchLoading && !hasResults ? (
          <div className="topbar-search-empty">Sin resultados</div>
        ) : null}
        {!searchLoading && hasResults
          ? searchSuggestions.map((suggestion, index) => {
              const categoryLabel = suggestion.categoryLabel || '';
              const showCategory =
                Boolean(categoryLabel) && categoryLabel !== lastCategory;
              if (showCategory) {
                lastCategory = categoryLabel;
              }
              const iconKey = suggestion.icon || suggestion.type;
              const IconComponent = SUGGESTION_ICON_MAP[iconKey] || FiSearch;
              const isActive = index === activeSuggestionIndex;
              const isDisabled =
                suggestion.isPlaceholder ||
                (!suggestion.href && !suggestion.searchValue);
              const itemId = `${suggestionsListId}-${suggestion.id}`;
              return (
                <React.Fragment key={suggestion.id}>
                  {showCategory ? (
                    <div className="topbar-search-group-label">{categoryLabel}</div>
                  ) : null}
                  <div
                    id={itemId}
                    role="option"
                    aria-selected={isActive}
                    aria-disabled={isDisabled || undefined}
                    className={`topbar-search-suggestion${
                      isActive ? ' is-active' : ''
                    }${suggestion.isPlaceholder ? ' is-placeholder' : ''}`}
                    onMouseEnter={() => setActiveSuggestionIndex(index)}
                    onMouseDown={event => {
                      event.preventDefault();
                      if (isDisabled) return;
                      handleSuggestionNavigate(suggestion);
                    }}
                  >
                    <span className="topbar-search-suggestion-icon" aria-hidden="true">
                      <IconComponent />
                    </span>
                    <span className="topbar-search-suggestion-body">
                      <span className="topbar-search-suggestion-title">
                        {suggestion.title}
                      </span>
                      {suggestion.subtitle ? (
                        <span className="topbar-search-suggestion-subtitle">
                          {suggestion.subtitle}
                        </span>
                      ) : null}
                    </span>
                  </div>
                </React.Fragment>
              );
            })
          : null}
      </div>
    );
  };

  const handleToggleNotifications = () => {
    setNotificationsOpen(prev => {
      const next = !prev;
      if (!prev) {
        setMenuOpen(false);
        if (typeof onCompanyNotificationsOpen === 'function') {
          onCompanyNotificationsOpen();
        }
      }
      return next;
    });
    if (isMobileSidebarOpen) {
      closeMobileSidebar();
    }
  };

  const handleNotificationNavigate = useCallback(
    () => {
      setNotificationsOpen(false);
      if (typeof onCompanyNotificationsOpen === 'function') {
        onCompanyNotificationsOpen();
      }
      navigate('/dashboard?section=notificaciones');
      if (isMobileSidebarOpen) {
        closeMobileSidebar();
      }
    },
    [onCompanyNotificationsOpen, navigate, isMobileSidebarOpen, closeMobileSidebar]
  );

  const closeProposalNotificationDetail = useCallback(() => {
    setProposalNotificationDetail({ open: false, item: null });
  }, []);

  const handleOpenProposalNotificationDetail = useCallback(
    item => {
      if (!item) return;
      handleNotificationNavigate(item);
    },
    [handleNotificationNavigate]
  );

  useEffect(() => {
    if (!proposalNotificationDetail.open) return;
    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        closeProposalNotificationDetail();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [proposalNotificationDetail.open, closeProposalNotificationDetail]);

  const renderNotificationMessage = (item, message, { variant = 'desktop' } = {}) => {
    if (item?.type === 'request_proposal') {
      const sanitized = sanitizeProposalNotificationMessage(message);
      return normalizeMojibake(sanitized) || sanitized;
    }
    const interactiveTypes = ['profile_view', 'publication_view'];
    if (!item || !interactiveTypes.includes(item?.type)) {
      return normalizeMojibake(message) || message;
    }
    const rawUsername =
      typeof item?.data?.viewerUsername === 'string' ? item.data.viewerUsername : '';
    const viewerUsername = rawUsername.trim();
    const rawViewerId =
      typeof item?.data?.viewerId === 'string'
        ? item.data.viewerId
        : typeof item?.data?.viewerId === 'number'
        ? String(item.data.viewerId)
        : '';
    const viewerIdValue = rawViewerId.trim();
    const rawUrl = typeof item?.data?.viewerUrl === 'string' ? item.data.viewerUrl.trim() : '';
    const viewerUrl =
      rawUrl ||
      (viewerUsername
        ? `/usuario/${encodeURIComponent(viewerUsername)}`
        : viewerIdValue
        ? `/usuario/${encodeURIComponent(viewerIdValue)}`
        : '');
    if (!viewerUrl) {
      return normalizeMojibake(message) || message;
    }
    const viewerLabel =
      normalizeMojibake(item?.data?.viewerLabel) ||
      viewerUsername ||
      viewerIdValue ||
      'Usuario';
    const text = normalizeMojibake(message) || '';
    const labelIndex = text.indexOf(viewerLabel);
    const className =
      variant === 'mobile'
        ? 'topbar-mobile-notification-viewer-link'
        : 'topbar-notification-viewer-link';
    const handleClick = event => {
      event.preventDefault();
      event.stopPropagation();
      handleNotificationNavigate(item, viewerUrl);
    };
    const handleKeyDown = event => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        event.stopPropagation();
        handleNotificationNavigate(item, viewerUrl);
      }
    };
    if (labelIndex === -1) {
      return (
        <>
          {text ? `${text} ` : ''}
          <span
            className={className}
            role="button"
            tabIndex={0}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
          >
            {viewerLabel}
          </span>
        </>
      );
    }
    const before = text.slice(0, labelIndex);
    const after = text.slice(labelIndex + viewerLabel.length);
    return (
      <>
        {before}
        <span
          className={className}
          role="button"
          tabIndex={0}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
        >
          {viewerLabel}
        </span>
        {after}
      </>
    );
  };

  const handleMarkAsRead = (event, item) => {
    event.preventDefault();
    event.stopPropagation();
    if (!item || item.read || typeof onNotificationRead !== 'function') return;
    onNotificationRead(item.id);
  };

  const handleMarkAsUnread = (event, item) => {
    event.preventDefault();
    event.stopPropagation();
    if (!item || !item.read || typeof onNotificationUnread !== 'function') return;
    onNotificationUnread(item.id);
  };

  const handleDeleteNotification = (event, item) => {
    event.preventDefault();
    event.stopPropagation();
    if (!item || typeof onNotificationDelete !== 'function') return;
    onNotificationDelete(item.id);
  };

  const handleMarkAllAsRead = () => {
    if (typeof onMarkAllNotificationsRead !== 'function') return;
    onMarkAllNotificationsRead();
  };

  const handleMobileSearchSubmit = event => {
    event.preventDefault();
    hideSuggestions({ blur: true });
    setNotificationsOpen(false);
    setMenuOpen(false);
    navigateToSearchResults();
  };

  const renderMobileSearchSection = () => (
    <section className="topbar-mobile-section topbar-mobile-section--search">
      <form className="topbar-mobile-search" onSubmit={handleMobileSearchSubmit}>
        <label className="topbar-mobile-search__label" htmlFor="topbar-mobile-search-input">
          Buscar en Flotte
        </label>
        <div className="topbar-mobile-search__field">
          <span className="topbar-mobile-search__icon" aria-hidden="true">
            <FiSearch />
          </span>
          <input
            ref={mobileSearchInputRef}
            id="topbar-mobile-search-input"
            type="search"
            placeholder="Buscar en Flotte"
            aria-label="Buscar en Flotte"
            value={quickSearchQuery}
            onChange={event => {
              setQuickSearchQuery(event.target.value);
              setIsSuggestionsOpen(false);
              setActiveSuggestionIndex(-1);
            }}
            autoComplete="off"
            spellCheck="false"
          />
          <button type="submit" className="topbar-mobile-search__submit">
            Buscar
          </button>
        </div>
      </form>
    </section>
  );

  const renderMobileSubnavSection = (title, items) => {
    if (!Array.isArray(items) || items.length === 0) return null;
    return (
      <section className="topbar-mobile-section" key={title}>
        <h3 className="topbar-mobile-section__title">{title}</h3>
        <ul className="topbar-mobile-links">
          {items.map(item => {
            const IconComponent = SUGGESTION_ICON_MAP[item.icon] || FiSearch;
            const isActive = isLinkActive(item.href);
            return (
              <li key={`${title}-${item.label}`}>
                <button
                  type="button"
                  className={`topbar-mobile-link${isActive ? ' is-active' : ''}`}
                  onClick={() => handleSubnavNavigate(item)}
                >
                  <span className="topbar-mobile-link__icon" aria-hidden="true">
                    <IconComponent />
                  </span>
                  <span className="topbar-mobile-link__body">
                    <span className="topbar-mobile-link__title">{item.label}</span>
                    {item.description ? (
                      <span className="topbar-mobile-link__desc">{item.description}</span>
                    ) : null}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </section>
    );
  };

  const renderMobileNotificationsSection = () => {
    if (!user) return null;
    return (
      <section className="topbar-mobile-section topbar-mobile-section--notifications">
        <div className="topbar-mobile-section__title-row">
          <h3 className="topbar-mobile-section__title">Notificaciones</h3>
          {unreadCount > 0 ? (
            <span className="topbar-mobile-badge">{unreadBadgeValue}</span>
          ) : null}
        </div>
        {notifications.length ? (
          <>
            <ul className="topbar-mobile-notifications">
              {notifications.slice(0, 10).map(item => {
              const itemId = item?.id || `mobile-notif-${Math.random()}`;
              const title = normalizeMojibake(item?.title) || 'Nueva reserva';
              const titleMeta = resolveNotificationTitleContent(item, title);
              const resolvedTitleText = titleMeta.titleText || title;
              const proposalStatusVariant = titleMeta.statusVariant;
              const proposalStatusLabel = titleMeta.statusLabel;
              const message =
                item?.message || 'Recibiste una nueva solicitud de reserva en Flotte.';
              const renderedMessage = renderNotificationMessage(item, message, { variant: 'mobile' });
              const baseTarget = resolveNotificationTarget(item);
              const proposalTargetUrl = isProposalNotification(item)
                ? resolveProposalNotificationUrl(item)
                : null;
              const hasClientContactInfo = proposalNotificationHasClientContact(item);
              const target = hasClientContactInfo ? '' : baseTarget;
              const NotificationIcon = resolveNotificationIcon(item?.type);
              const statusLabel = item?.read ? 'Leida' : 'Nueva';
              const statusVariant = item?.read ? 'read' : 'new';
              const createdAtDate = item?.createdAt ? new Date(item.createdAt) : null;
              const isoDate = createdAtDate ? createdAtDate.toISOString() : undefined;
              const formattedDate = formatNotificationDate(createdAtDate || item?.createdAt);
              return (
                <li
                  key={itemId}
                  className={`topbar-mobile-notification${
                    item?.read ? ' is-read' : ' is-unread'
                  }`}
                >
                  <button
                    type="button"
                    className="topbar-mobile-notification__main"
                    onClick={() => handleNotificationNavigate(item, target)}
                  >
                    <span className="topbar-mobile-notification__icon" aria-hidden="true">
                      <NotificationIcon />
                    </span>
                    <span className="topbar-mobile-notification__body">
                      <span className="topbar-mobile-notification__title">
                        <span className="topbar-mobile-notification__title-main">
                          {!item?.read ? (
                            <span className="topbar-notification-dot" aria-hidden="true" />
                          ) : null}
                          <span className="topbar-mobile-notification__title-text">
                            {resolvedTitleText}
                          </span>
                        </span>
                      </span>
                      <span className="topbar-mobile-notification__message">
                        {renderedMessage}
                      </span>
                      <span className="topbar-mobile-notification__meta">
                        <span
                          className={`topbar-mobile-notification__status topbar-mobile-notification__status--${statusVariant}`}
                        >
                          {statusLabel}
                        </span>
                        <time
                          className="topbar-mobile-notification__date"
                          dateTime={isoDate}
                        >
                          {formattedDate}
                        </time>
                        {proposalStatusVariant && proposalStatusLabel ? (
                          <span
                            className={`topbar-notification-status-pill topbar-notification-status-pill--${proposalStatusVariant}`}
                          >
                            {proposalStatusLabel}
                          </span>
                        ) : null}
                      </span>
                    </span>
                  </button>
                  <div className="topbar-mobile-notification__actions">
                    {isProposalNotification(item) ? (
                      <button
                        type="button"
                        className="topbar-mobile-notification__action"
                        onClick={event => {
                          event.stopPropagation();
                          if (proposalNotificationHasClientContact(item)) {
                            handleOpenProposalNotificationDetail(item);
                          } else {
                            handleNotificationNavigate(item, proposalTargetUrl);
                          }
                        }}
                      >
                        <FiEye aria-hidden="true" />
                        <span>Ver propuesta</span>
                      </button>
                    ) : null}
                    {item?.read ? (
                      <button
                        type="button"
                        className="topbar-mobile-notification__action topbar-mobile-notification__action--ghost"
                        onClick={event => handleMarkAsUnread(event, item)}
                      >
                        <FiRotateCcw aria-hidden="true" />
                        <span>Marcar como no leida</span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="topbar-mobile-notification__action"
                        onClick={event => handleMarkAsRead(event, item)}
                      >
                        <FiCheck aria-hidden="true" />
                        <span>Marcar leida</span>
                      </button>
                    )}
                    <button
                      type="button"
                      className="topbar-mobile-notification__action topbar-mobile-notification__action--icon"
                      onClick={event => handleDeleteNotification(event, item)}
                      aria-label="Eliminar notificacion"
                    >
                      <FiTrash2 aria-hidden="true" />
                    </button>
                  </div>
                </li>
              );
              })}
            </ul>
            {notifications.length > 10 ? (
              <div className="topbar-mobile-notifications-footer">
                <button
                  type="button"
                  className="topbar-notifications-more"
                  onClick={() => {
                    setIsMobileSidebarOpen(false);
                    setNotificationsOpen(false);
                    navigate('/dashboard?section=notificaciones');
                  }}
                >
                  Ver más notificaciones
                </button>
              </div>
            ) : null}
          </>
        ) : (
          <p className="topbar-mobile-empty">{emptyNotificationsCopy}</p>
        )}
      </section>
    );
  };

  const renderMobileAccountSection = () => {
    if (!user) return null;
    return (
      <section className="topbar-mobile-section topbar-mobile-section--account">
        <h3 className="topbar-mobile-section__title">Cuenta</h3>
        <div className="topbar-mobile-account">
          <div className="topbar-mobile-account__summary">
            <span className="user-icon-avatar-ring" aria-hidden="true">
              <span
                className={`user-icon-avatar${hasUserAvatar ? ' has-image' : ''}`}
              >
                {hasUserAvatar ? (
                  <img src={userAvatarUrl} alt="" loading="lazy" />
                ) : (
                  <span className="topbar-avatar-initials" aria-hidden="true">
                    {userInitials}
                  </span>
                )}
              </span>
            </span>
            <div>
              <span className="topbar-mobile-account__name">{primaryName}</span>
              <span className="topbar-mobile-account__role">
                {isAdmin ? 'Administrador' : 'Mi cuenta'}
              </span>
            </div>
          </div>
          <ul className="topbar-mobile-links topbar-mobile-links--compact">
            {accountMenuOptions.map(option => (
              <li key={`account-${option.key}`}>
                <button
                  type="button"
                  className={`topbar-mobile-link${
                    option.variant === 'danger' ? ' topbar-mobile-link--danger' : ''
                  }`}
                  onClick={() => handleAccountMenuSelect(option)}
                >
                  <span className="topbar-mobile-link__icon" aria-hidden="true">
                    {option.icon}
                  </span>
                  <span className="topbar-mobile-link__body">
                    <span className="topbar-mobile-link__title">{option.label}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </section>
    );
  };

  const renderMobileGuestSection = () => (
    <section className="topbar-mobile-section topbar-mobile-section--auth">
      <h3 className="topbar-mobile-section__title">Bienvenido a Flotte</h3>
      <p className="topbar-mobile-auth__text">
        Accede para administrar tu cuenta o unete a nuestra comunidad.
      </p>
      <div className="topbar-mobile-auth-actions">
        <Link
          to="/login"
          className="topbar-mobile-auth-link topbar-mobile-auth-link--primary"
          onClick={closeMobileSidebar}
        >
          Iniciar sesión
        </Link>
        <Link
          to="/register"
          className="topbar-mobile-auth-link"
          onClick={closeMobileSidebar}
        >
          Registrarme
        </Link>
      </div>
    </section>
  );

  const proposalDetailPayload = proposalNotificationDetail.item || {};
  const proposalDetailContactName =
    proposalDetailPayload.contactName || 'Contacto no disponible';
  const proposalDetailContactEmail = proposalDetailPayload.contactEmail || '';
  const proposalDetailContactPhone = proposalDetailPayload.contactPhone || '';

  const resolveNotificationTitleContent = (item, rawTitle) => {
    const normalizedTitle = normalizeMojibake(rawTitle) || rawTitle || '';
    const baseResult = {
      titleText: normalizedTitle,
      statusVariant: '',
      statusLabel: ''
    };
    if (!isProposalNotification(item) || !normalizedTitle) {
      return baseResult;
    }
    const lower = normalizedTitle.toLowerCase();
    const statusMap = [
      { keyword: 'aceptada', variant: 'accepted' },
      { keyword: 'rechazada', variant: 'rejected' }
    ];
    const matchedStatus = statusMap.find(entry => lower.includes(entry.keyword));
    if (!matchedStatus) {
      return baseResult;
    }
    const regex = new RegExp(matchedStatus.keyword, 'i');
    const match = normalizedTitle.match(regex);
    if (!match) {
      return baseResult;
    }
    const cleanedTitle = normalizedTitle.replace(regex, ' ').replace(/\s{2,}/g, ' ').trim();
    return {
      titleText: cleanedTitle || normalizedTitle,
      statusVariant: matchedStatus.variant,
      statusLabel: match[0]
    };
  };

  return (
    <>
      <header
        className={`topbar${isMobileSidebarOpen ? ' topbar--mobile-open' : ''}${
          hasContextBar ? ' topbar--has-context' : ''
        }`}
      >
        <div className="topbar-row">
          <div className="topbar-left">
            <Link to={logoHref} className="topbar-logo-group">
              <PremiumLogo
                className="topbar-premium-logo"
                size={40}
                ariaLabel="Logo de Flotte"
              />
            </Link>
            {user ? (
              <div
                className={`topbar-search topbar-search--compact${isSuggestionsOpen ? ' is-open' : ''}`}
                ref={searchContainerRef}
              >
                <form
                  className="topbar-search-form"
                  role="search"
                  onSubmit={handleQuickSearchSubmit}
                >
                  <span className="topbar-search-icon" aria-hidden="true">
                    <FiSearch />
                  </span>
                  <input
                    ref={searchInputRef}
                    type="search"
                    className="topbar-search-input"
                    placeholder="Buscar en Flotte"
                    aria-label="Buscar en Flotte"
                    autoComplete="off"
                    spellCheck="false"
                    value={quickSearchQuery}
                    onChange={event => {
                      setQuickSearchQuery(event.target.value);
                      setIsSuggestionsOpen(true);
                      setActiveSuggestionIndex(-1);
                    }}
                    onFocus={() => setIsSuggestionsOpen(true)}
                    onKeyDown={handleSearchInputKeyDown}
                    onBlur={event => {
                      const { relatedTarget } = event;
                      if (relatedTarget && searchContainerRef.current?.contains(relatedTarget)) {
                        return;
                      }
                      hideSuggestions();
                    }}
                    aria-controls={suggestionsListId}
                    aria-activedescendant={
                      isSuggestionsOpen &&
                      activeSuggestionIndex >= 0 &&
                      searchSuggestions[activeSuggestionIndex]
                        ? `${suggestionsListId}-${searchSuggestions[activeSuggestionIndex].id}`
                        : undefined
                    }
                    aria-autocomplete="list"
                  />
                </form>
                {renderSearchSuggestions()}
              </div>
            ) : null}
          </div>
          <div className="topbar-center">
            {user && mainNavigationLinks.length ? (
              <nav className="topbar-main-nav" aria-label="Navegación principal">
                {mainNavigationLinks.map(link => {
                  const isActive = isLinkActive(link.href);
                  return (
                    <Link
                      key={link.key}
                      to={link.href}
                      className={`topbar-main-nav__link${isActive ? ' is-active' : ''}`}
                      aria-current={isActive ? 'page' : undefined}
                      onClick={handleMainNavLinkClick}
                    >
                      {link.label}
                    </Link>
                  );
                })}
              </nav>
            ) : (
              !user && (
                <span className="topbar-guest-label topbar-guest-label--center" aria-hidden="true">
                  Flotte
                </span>
              )
            )}
          </div>
          <div className="topbar-right">
            <button
              type="button"
              className={`topbar-mobile-toggle${isMobileSidebarOpen ? ' is-open' : ''}`}
              onClick={isMobileSidebarOpen ? closeMobileSidebar : openMobileSidebar}
              aria-label={
                isMobileSidebarOpen ? 'Cerrar menu de navegación' : 'Abrir menu de navegación'
              }
              aria-expanded={isMobileSidebarOpen}
              aria-controls="topbar-mobile-sidebar"
            >
              {isMobileSidebarOpen ? <FiX aria-hidden="true" /> : <FiMenu aria-hidden="true" />}
            </button>
            <nav className="topbar-nav">
              {user && onToggleTheme && SHOULD_RENDER_THEME_TOGGLE && (
                <button
                  type="button"
                  className="topbar-theme-toggle"
                  onClick={onToggleTheme}
                  aria-label={themeToggleLabel}
                  title={themeToggleLabel}
                >
                  {isDarkTheme ? <FaSun aria-hidden="true" /> : <FaMoon aria-hidden="true" />}
                </button>
              )}
              {!user ? (
                <div className="topbar-auth-actions">
                  <Link to="/login" className="topbar-auth-link">
                    Iniciar sesión
                  </Link>
                  <Link
                    to="/register"
                    className="topbar-auth-link topbar-auth-link--primary"
                  >
                    Registrarse
                  </Link>
                </div>
              ) : (
                <div className="topbar-actions">
                  <div className="topbar-quick-cluster">
                    <div className="topbar-quick-item">
                      <Link
                        to="/referrals"
                        className="topbar-action topbar-icon-button topbar-toolbar-icon topbar-quick-button topbar-referral-button"
                        aria-label="Programa de referidos"
                        title="Programa de referidos"
                      >
                        <FiGift aria-hidden="true" />
                      </Link>
                    </div>
                    <div className="topbar-quick-item">
                      <div
                        className={`topbar-notifications-wrapper${
                          notificationsOpen ? ' is-open' : ''
                        }`}
                        ref={notificationsRef}
                      >
                        <button
                          type="button"
                          className={`topbar-action topbar-icon-button topbar-toolbar-icon topbar-quick-button topbar-toolbar-icon--bell topbar-notification-btn${
                            unreadCount > 0 ? ' has-unread' : ''
                          }`}
                          aria-label={
                            unreadCount > 0
                              ? `Notificaciones, ${formatUnreadNotificationsLabel(unreadCount)}`
                              : 'Notificaciones'
                          }
                          title={
                            unreadCount > 0
                              ? formatUnreadNotificationsLabel(unreadCount)
                              : 'Notificaciones'
                          }
                          onClick={handleToggleNotifications}
                        >
                          <FiBell aria-hidden="true" />
                          <span
                            className={`topbar-notification-indicator${
                              unreadCount > 0 ? ' is-visible' : ''
                            }`}
                            aria-hidden="true"
                          />
                        </button>
                        {notificationsOpen ? (
                          <div
                            className="topbar-notifications-panel card"
                            role="menu"
                            aria-label="Notificaciones de la empresa"
                          >
                        <header className="topbar-notifications-header">
                          <div className="topbar-notifications-title">
                            <strong>Notificaciones</strong>
                            {unreadCount > 0 ? (
                              <span className="topbar-notifications-count">{unreadBadgeValue}</span>
                            ) : null}
                          </div>
                          {notifications.length ? (
                            <button
                              type="button"
                              className="topbar-notifications-mark-all"
                              onClick={handleMarkAllAsRead}
                              disabled={!unreadCount}
                            >
                              Marcar todas leídas
                            </button>
                          ) : null}
                        </header>
                        <span className="topbar-notifications-divider" aria-hidden="true" />
                        {notifications.length ? (
                          <>
                            <ul className="topbar-notifications-list">
                              {notifications.slice(0, 10).map(item => {
                              const itemId = item?.id || `notif-${Math.random()}`;
                              const title =
                                normalizeMojibake(item?.title) || 'Nueva reserva';
                              const titleMeta = resolveNotificationTitleContent(item, title);
                              const resolvedTitleText = titleMeta.titleText || title;
                              const statusVariant = titleMeta.statusVariant;
                              const statusLabel = titleMeta.statusLabel;
                              const message =
                                normalizeMojibake(item?.message) ||
                                'Recibiste una nueva solicitud de reserva en Flotte.';
                              const renderedMessage = renderNotificationMessage(item, message);
                              const baseTarget = resolveNotificationTarget(item);
                              const proposalTargetUrl = isProposalNotification(item)
                                ? resolveProposalNotificationUrl(item)
                                : null;
                              const hasClientContactInfo = proposalNotificationHasClientContact(item);
                              const target = hasClientContactInfo ? '' : baseTarget;
                              const NotificationIcon = resolveNotificationIcon(item?.type);
                              const isRead = Boolean(item?.read);
                              const createdAtDate = item?.createdAt
                                ? new Date(item.createdAt)
                                : null;
                              const isoDate = createdAtDate ? createdAtDate.toISOString() : undefined;
                              const formattedDate = formatNotificationDate(createdAtDate || item?.createdAt);
                              const primaryActionHandler = isRead
                                ? event => handleMarkAsUnread(event, item)
                                : event => handleMarkAsRead(event, item);
                              const primaryActionLabel = isRead
                                ? 'Marcar como no leída'
                                : 'Marcar como leída';
                              return (
                                <li
                                  key={itemId}
                                  className={`topbar-notification-item${
                                    item?.read ? ' is-read' : ''
                                  }`}
                                >
                                  <div className="topbar-notification-row">
                                    <button
                                      type="button"
                                      className="topbar-notification-link"
                                      onClick={() => handleNotificationNavigate(item, target)}
                                    >
                                      <span className="topbar-notification-icon" aria-hidden="true">
                                        <NotificationIcon />
                                      </span>
                                      <div className="topbar-notification-body">
                                        <span className="topbar-notification-title">
                                          {!item?.read ? (
                                            <span className="topbar-notification-dot" aria-hidden="true" />
                                          ) : null}
                                          <span className="topbar-notification-title-text">
                                            {resolvedTitleText}
                                          </span>
                                        </span>
                                        <span className="topbar-notification-message">
                                          {renderedMessage}
                                        </span>
                                      </div>
                                    </button>
                                    <div className="topbar-notification-aside">
                                      <div className="topbar-notification-meta">
                                        <time
                                          className="topbar-notification-date"
                                          dateTime={isoDate}
                                        >
                                          {formattedDate}
                                        </time>
                                        {statusVariant && statusLabel ? (
                                          <span
                                            className={`topbar-notification-status-pill topbar-notification-status-pill--${statusVariant}`}
                                          >
                                            {statusLabel}
                                          </span>
                                        ) : null}
                                      </div>
                                      <div className="topbar-notification-quick-actions">
                                        {isProposalNotification(item) &&
                                        item?.data?.status !== 'rejected' ? (
                                          <button
                                            type="button"
                                            className="topbar-notification-quick-action"
                                            onClick={event => {
                                              event.stopPropagation();
                                              if (proposalNotificationHasClientContact(item)) {
                                                handleOpenProposalNotificationDetail(item);
                                              } else {
                                                handleNotificationNavigate(item, proposalTargetUrl);
                                              }
                                            }}
                                            aria-label="Ver propuesta"
                                          >
                                            <FiEye aria-hidden="true" />
                                          </button>
                                        ) : null}
                                        <button
                                          type="button"
                                          className="topbar-notification-quick-action topbar-notification-quick-action--check"
                                          onClick={primaryActionHandler}
                                          aria-label={primaryActionLabel}
                                        >
                                          <FiCheckCircle aria-hidden="true" />
                                        </button>
                                        <button
                                          type="button"
                                          className="topbar-notification-quick-action topbar-notification-quick-action--danger"
                                          onClick={event => handleDeleteNotification(event, item)}
                                          aria-label="Eliminar notificación"
                                        >
                                          <FiTrash2 aria-hidden="true" />
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </li>
                              );
                              })}
                            </ul>
                            <div className="topbar-notifications-footer">
                              <button
                                type="button"
                                className="topbar-notifications-more"
                                onClick={() => {
                                  setNotificationsOpen(false);
                                  navigate('/dashboard?section=notificaciones');
                                }}
                              >
                                Ver todas las notificaciones
                              </button>
                            </div>
                          </>
                        ) : (
                          <div className="topbar-notifications-empty">
                            {emptyNotificationsCopy}
                          </div>
                        )}
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <div className="topbar-quick-item">
                      <div
                        className={`topbar-user-dropdown${menuOpen ? ' is-open' : ''}`}
                        ref={menuRef}
                      >
                        <button
                          type="button"
                          className="topbar-avatar-trigger"
                          aria-label={menuOpen ? 'Cerrar menú de usuario' : 'Abrir menú de usuario'}
                          aria-expanded={menuOpen}
                          aria-haspopup="true"
                          onClick={() => {
                            setMenuOpen(v => !v);
                          }}
                        >
                          <span className="topbar-avatar-circle" aria-hidden="true">
                            {hasUserAvatar ? (
                              <img src={userAvatarUrl} alt="" loading="lazy" />
                            ) : (
                              <span className="topbar-avatar-initials" aria-hidden="true">
                                {userInitials}
                              </span>
                            )}
                          </span>
                        </button>
                        {menuOpen && (
                          <div className="user-dropdown-menu">
                            <div className="user-dropdown-header">
                              <div className="user-dropdown-avatar rounded-full" aria-hidden="true">
                                <span
                                  className={`user-dropdown-avatar-icon rounded-full${
                                    hasUserAvatar ? ' has-image' : ''
                                  }`}
                                >
                                  {hasUserAvatar ? (
                                    <img src={userAvatarUrl} alt="" loading="lazy" />
                                  ) : (
                                    <span className="topbar-avatar-initials" aria-hidden="true">
                                      {userInitials}
                                    </span>
                                  )}
                                </span>
                              </div>
                              <div className="user-dropdown-meta">
                                <span className="user-dropdown-name">{primaryName}</span>
                                <span className="user-dropdown-role">
                                  {isAdmin ? 'Administrador' : 'Mi cuenta'}
                                </span>
                              </div>
                            </div>
                            <div className="user-dropdown-list" role="menu">
                              {mainAccountOptions.map(option => (
                                <button
                                  key={option.key}
                                  type="button"
                                  className="user-dropdown-item"
                                  onClick={() => handleAccountMenuSelect(option)}
                                >
                                  <span className="user-dropdown-item__icon" aria-hidden="true">
                                    {option.icon}
                                  </span>
                                  <span className="user-dropdown-item__label">{option.label}</span>
                                </button>
                              ))}
                            </div>
                            {adminAccountOption ? (
                              <>
                                <div className="user-dropdown-divider" role="separator" />
                                <div className="user-dropdown-list" role="menu">
                                  <button
                                    type="button"
                                    className="user-dropdown-item user-dropdown-item--admin"
                                    onClick={() => handleAccountMenuSelect(adminAccountOption)}
                                  >
                                    <span className="user-dropdown-item__icon" aria-hidden="true">
                                      {adminAccountOption.icon}
                                    </span>
                                    <span className="user-dropdown-item__label">
                                      {adminAccountOption.label}
                                    </span>
                                  </button>
                                </div>
                              </>
                            ) : null}
                            {signOutOption ? (
                              <>
                                <div className="user-dropdown-divider" role="separator" />
                                <div className="user-dropdown-list" role="menu">
                                  <button
                                    type="button"
                                    className="user-dropdown-item user-dropdown-item--danger"
                                    onClick={() => handleAccountMenuSelect(signOutOption)}
                                  >
                                    <span className="user-dropdown-item__icon" aria-hidden="true">
                                      {signOutOption.icon}
                                    </span>
                                    <span className="user-dropdown-item__label">
                                      {signOutOption.label}
                                    </span>
                                  </button>
                                </div>
                              </>
                            ) : null}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </nav>
          </div>
        </div>
        {hasContextBar ? renderContextBar() : null}
        {isMobileSidebarOpen ? (
        <div
          className="topbar-mobile-sidebar is-open"
          id="topbar-mobile-sidebar"
          role="dialog"
          aria-modal="true"
          aria-labelledby="topbar-mobile-sidebar-title"
        >
          <button
            type="button"
            className="topbar-mobile-sidebar__backdrop"
            onClick={closeMobileSidebar}
            aria-label="Cerrar menu de navegación"
          />
          <aside className="topbar-mobile-sidebar__panel">
            <header className="topbar-mobile-sidebar__header">
              <div className="topbar-mobile-sidebar__brand">
                <img src="/logo192.png" alt="" aria-hidden="true" />
                <span id="topbar-mobile-sidebar-title">Flotte</span>
              </div>
              <button
                type="button"
                className="topbar-mobile-close"
                onClick={closeMobileSidebar}
                aria-label="Cerrar menu de navegación"
              >
                <FiX aria-hidden="true" />
              </button>
            </header>
            <div className="topbar-mobile-sidebar__content">
              {renderMobileSearchSection()}
              {user ? (
                <>
                  {renderMobileSubnavSection('Home', homeSubnavItems)}
                  {renderMobileSubnavSection('Operaciones', operationsSubnavItems)}
                  {renderMobileSubnavSection('Estadísticas', statisticsSubnavItems)}
                  {renderMobileSubnavSection('Ayuda', helpSubnavItems)}
                  {renderMobileSubnavSection('Promocionar', promoteSubnavItems)}
                  {renderMobileNotificationsSection()}
                  {renderMobileAccountSection()}
                </>
              ) : (
                <>
                  {renderMobileSubnavSection('Ayuda', helpSubnavItems)}
                  {renderMobileGuestSection()}
                </>
              )}
            </div>
          </aside>
        </div>
      ) : null}
    </header>
      {proposalNotificationDetail.open ? (
        <div
          className="topbar-proposal-detail-backdrop"
          role="presentation"
          onClick={closeProposalNotificationDetail}
        >
          <div
            className="topbar-proposal-detail"
            role="dialog"
            aria-modal="true"
            aria-labelledby="topbar-proposal-detail-title"
            onClick={event => event.stopPropagation()}
          >
            <header className="topbar-proposal-detail__header">
              <div>
                <h3 id="topbar-proposal-detail-title">Tu propuesta fue aceptada</h3>
                <p>Ahora ponte en contacto con el cliente para coordinar la operación.</p>
              </div>
              <button
                type="button"
                className="topbar-proposal-detail__close"
                onClick={closeProposalNotificationDetail}
                aria-label="Cerrar"
              >
                <FiX aria-hidden="true" />
              </button>
            </header>
            <div className="topbar-proposal-detail__body">
              <p className="topbar-proposal-detail__message">Estos son sus datos:</p>
              <ul className="topbar-proposal-detail__list">
                <li>
                  <span>Contacto</span>
                  <strong>{proposalDetailContactName}</strong>
                </li>
                {proposalDetailContactEmail ? (
                  <li>
                    <span>Correo</span>
                    <a href={`mailto:${proposalDetailContactEmail}`}>
                      {proposalDetailContactEmail}
                    </a>
                  </li>
                ) : null}
                {proposalDetailContactPhone ? (
                  <li>
                    <span>Teléfono</span>
                    <a href={`tel:${proposalDetailContactPhone}`}>
                      {proposalDetailContactPhone}
                    </a>
                  </li>
                ) : null}
              </ul>
            </div>
            <footer className="topbar-proposal-detail__footer">
              <button
                type="button"
                className="topbar-proposal-detail__primary"
                onClick={closeProposalNotificationDetail}
              >
                Entendido
              </button>
            </footer>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default Topbar;
